/* Shared runtime context for domain modules.
 * Module-level caches in outline.js are wired as getter/setters on this
 * object so domain modules can read and write them without circular imports.
 * Function references (render, refreshSection, etc.) are assigned inside
 * initOutline() before any async callbacks can fire.
 */
import { dataLoader } from '../services/dataLoader.js'

export var ctx = {}

// Shared data loader — promise-level deduplication for all sheet-scoped cache types.
// outline.js calls ctx.resetLoader() from resetCaches() and resetProjectScopedCaches().
ctx.loader      = dataLoader
ctx.resetLoader = (prefix) => dataLoader.reset(prefix)

// The full-bleed workspace overlays (Billing, CRM, Roadmap & Bugs, Pulse, and the legacy
// Scheduler surface) each mount as a sibling positioned above the Projects outline and are
// toggled open/closed by these CustomEvents (see SiteNav.jsx and each workspace's own
// open-*/close-* listener). Any outline-targeting navigation — jumping to a project, a section,
// a filter — must close all of them first, or the change happens on the outline underneath
// while the visible workspace never switches. Never touches SiteNav's own open/collapsed state.
ctx.closeAllWorkspaces = function closeAllWorkspaces() {
  ;['close-billing', 'close-tracker', 'close-pulse', 'close-team', 'close-crm', 'close-studio', 'close-permitting', 'close-construction-hub', 'close-finance', 'close-templates-workspace'].forEach(name => {
    window.dispatchEvent(new CustomEvent(name))
  })
}

// Contacts module caches (Round 8)
// undefined = not loaded, null = in-flight, [..] = loaded
// Populated by ContactsPanel on first open and busted on mutations.
ctx.contactsCache    = undefined   // Contact records
ctx.contactsOrgCache = undefined   // Organization records

// Meetings module cache (project-scoped, managed by MeetingsSection — mirrors threadCache)
// undefined = not loaded, null = in-flight, [..] = loaded. Read by the breadcrumb + ZoomTitle.
ctx.meetingCache = undefined   // Meeting records for the current project

// Team module caches (project-scoped, managed by TeamSection — mirrors meetingCache).
// undefined = not loaded, null = in-flight, [..]/{...} = loaded. Read by the breadcrumb + ZoomTitle.
ctx.teamRows         = undefined   // raw Project Teams rows for the current project
ctx.teamOrganizations = undefined  // full Organizations list (shared across groups + the picker)
ctx.teamClientOrgId  = undefined   // the project's Client organization id (Projects.Client)
ctx.teamProjectConsultants = undefined // Project Consultants rows (candidate/awarded subconsultants) — services/projectConsultants.js
ctx.teamGrouped      = null        // computed { alderJames, client, subconsultants, candidates } — see services/team.js
ctx.consultantRfpCache = null      // { rfps, proposals } for the current project's candidates — services/consultantRfps.js

// Parcel Research caches (CT-020, React-owned by ParcelDetailSection / ParcelResearchTool)
ctx.parcelDetailCache   = {}   // keyed by parcelId: { parcel, zone, jurisdiction }
ctx.historicalPermitCache = {} // keyed by parcelId: Historical Permit records (Existing Approvals)
ctx.agencyCache         = {}   // keyed by jurisdictionId: agency Organization records

// Primary parcel for the current project — lowest Project Parcel ID (no dedicated
// flag field yet; see docs/PARCEL_RESEARCH.md backlog). Reads the engine's
// per-project parcelsCache, which DesignSection also populates.
Object.defineProperty(ctx, 'primaryParcel', {
  get() {
    const list = (ctx.parcelsCache && Array.isArray(ctx.parcelsCache)) ? ctx.parcelsCache : []
    if (!list.length) return null
    return list.slice().sort((a, b) =>
      (Number(a.fields['Project Parcel ID']) || 9999) - (Number(b.fields['Project Parcel ID']) || 9999)
    )[0]
  },
})
